import _, { get } from 'lodash';
import * as Papa from 'papaparse';
import { ZodError, ZodIssue, defaultErrorMap, z } from 'zod';
import {
  BankDebitMethod,
  Country as CountryEnum,
  Currency,
  DefaultLanguage,
  EmailMarketingOption,
  EnableSendRecurringChargeEmailsOption,
  GateGeoCheckInByDistance,
  IsRemainingSpotCountPublicOption,
  Language,
  Location,
  TaxPricingOption,
  allowedCurrenciesPerCountry,
} from '../types/location';
import { mapCsvToLocation } from './locationParseDetails';
import {
  emailRegex,
  phoneNumberRegex,
  stripeStatementDescriptorRegex,
  validateABN,
  validateJapanVAT,
  validatePostalCode,
  validateVAT,
} from './validation';

export const BankDebitMethodSchema = z.nativeEnum(BankDebitMethod).optional();

export const LocationSchema = z
  .object({
    Name: z.string().min(1),
    RegionName: z.string().min(1),
    PrimaryLanguage: z.nativeEnum(Language),
    DefaultLanguage: z.nativeEnum(DefaultLanguage),
    Country: z.nativeEnum(CountryEnum),
    AddressLine1: z.string().min(1),
    AddressLine2: z.string().optional(),
    AddressLine3: z.string().optional(),
    AddressCity: z.string().min(1),
    StateProvince: z.string().min(1),
    PhoneNumber: z
      .string()
      .min(1)
      .refine((value) => (value ? phoneNumberRegex.test(value) : true), {
        message: `Phone number must be entered in the format: '+999999999'. Up to 15 digits allowed.`,
      }),
    PostalCode: z.string().optional(),
    EmailAddress: z
      .string()
      .min(1)
      .superRefine((value, ctx) => {
        const isValid = emailRegex.test(value);
        if (!isValid && value) {
          ctx.addIssue({
            message: `Value cannot be ${value}`,
            code: 'custom',
          });
        }
      }),
    ChargeBackEmail: z
      .string()
      .min(1)
      .superRefine((value, ctx) => {
        const isValid = emailRegex.test(value);
        if (!isValid && value) {
          ctx.addIssue({
            message: `Value cannot be ${value}`,
            code: 'custom',
          });
        }
      }),
    CurrencyCode: z.string(),
    IsWaitlistPositionPublic: z.boolean(),
    IsWaitlistCountPublic: z.boolean(),
    IsRemainingSpotCountPublic: z.nativeEnum(IsRemainingSpotCountPublicOption),
    EnableChangeSpots: z.boolean(),
    EnableClasspass: z.boolean().optional(),
    EnableThirdPartyChangeSpots: z.boolean(),
    DailyReservationLimit: z.number().optional().nullable(),
    EnableGeoCheckIn: z.boolean(),
    GateGeoCheckInByDistance: z.nativeEnum(GateGeoCheckInByDistance),
    GeoCheckInDistance: z.number().optional().nullable(),
    EnableRoyaltyFeePlatform: z.boolean(),
    PrimaryClassroom: z.string().min(1),
    EnablePreCheckedNewsletterSubscription: z.nativeEnum(EmailMarketingOption),
    LateCancelFee: z.number(),
    NoShowFee: z.number(),
    UseTaxInclusivePricing: z.nativeEnum(TaxPricingOption).optional(),
    BusinessNumber: z.string().optional().nullable(),
    StripeAccountId: z
      .string({ required_error: 'This field is required' })
      .min(1)
      .regex(
        /^(?:acct_[a-zA-Z0-9]{16})?$/,
        'This field cannot contain special characters,must start with acct_ and contain 21 characters.',
      ),
    StripeStatementDescriptor: z.string().superRefine((value, ctx) => {
      if (!value || value.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'This field is required',
          path: ['StripeStatementDescriptor'],
        });
      } else if (!stripeStatementDescriptorRegex.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'This field should contain between 5 and 22 characters and at least one letter.',
          path: ['StripeStatementDescriptor'],
        });
      }
    }),
    PercentageFee: z.number().min(0).max(25),
    FlatFee: z.number().min(0).max(25),
    TerminalPercentageFee: z.number().min(0).max(25),
    TerminalFlatFee: z.number().min(0).max(25),
    BankDebitMethod: BankDebitMethodSchema,
    DirectDebitPercentageFee: z.number().min(0).max(25).optional(),
    DirectDebitFlatFee: z.number().min(0).max(25).optional(),
    EnableSendRecurringChargeEmails: z.nativeEnum(
      EnableSendRecurringChargeEmailsOption,
    ),
  })
  .superRefine(({ Country, BusinessNumber, PostalCode }, ctx) => {
    if (Country === CountryEnum.Australia) {
      if (!!BusinessNumber && !validateABN(BusinessNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid AUS Business Number format',
          path: ['BusinessNumber'],
        });
      }
    }

    if (Country === CountryEnum['United Kingdom']) {
      if (!!BusinessNumber && !validateVAT(BusinessNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Vat Number format',
          path: ['BusinessNumber'],
        });
      }
    }

    if (Country === CountryEnum['Japan']) {
      if (!!BusinessNumber && !validateJapanVAT(BusinessNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Vat Number format',
          path: ['BusinessNumber'],
        });
      }
    }

    if (Country !== CountryEnum['United Arab Emirates']) {
      if (!PostalCode || PostalCode.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Postal Code is required for countries other than AE',
          path: ['PostalCode'],
        });
      }
    }
  });

const LocationArraySchema = z.array(LocationSchema);

const validateAndParseEntry = (results: Record<string, string>[]) => {
  const parsedLocations: Location[] = [];
  const parsedResults: Partial<Location>[] = [];

  results.forEach((result: Record<string, string>) => {
    let location: Partial<Location> = {};
    let parsedResult: Partial<Location> = {};
    Object.keys(result).forEach((key) => {
      const cleanKey = key.endsWith('*')
        ? key.substring(0, key.length - 1)
        : key;

      if (mapCsvToLocation[cleanKey]) {
        const mappedValue = mapCsvToLocation[cleanKey].mapper?.(result[key]);
        location = {
          ...(location || {}),
          [mapCsvToLocation[cleanKey].key]: mappedValue,
        };
        parsedResult = {
          ...(parsedResult || {}),
          [mapCsvToLocation[cleanKey].key]: result[key],
        };
      }
    });
    // A blank method leaves existing gateway configuration and fees untouched.
    if (!location.BankDebitMethod) {
      delete location.DirectDebitPercentageFee;
      delete location.DirectDebitFlatFee;
    }
    parsedLocations.push(location as Location);
    parsedResults.push(parsedResult);
  });

  let errors: ZodIssue[] = [];
  // Standard validation of ZodSchema
  try {
    LocationArraySchema.parse(parsedLocations, {
      errorMap: (error, _ctx) => {
        const value = get(parsedResults, error.path.join('.'));

        if (
          (error.code === z.ZodIssueCode.invalid_type ||
            error.code === z.ZodIssueCode.too_small) &&
          !value
        ) {
          return {
            message: `This field is required`,
          };
        } else if (
          (error.code === z.ZodIssueCode.invalid_type &&
            (error.expected === 'number' || error.expected === 'boolean') &&
            isNaN(value)) ||
          (error.code === z.ZodIssueCode.invalid_enum_value && !!value)
        ) {
          return {
            message: `Value cannot be ${get(
              parsedResults,
              error.path.join('.'),
            )}`,
          };
        } else if (error.message) {
          return { message: error.message };
        }
        return defaultErrorMap(error, _ctx);
      },
    });
  } catch (e) {
    const zodError = e as ZodError;
    errors = zodError.errors;
  }

  // Additional validation for Country, CurrencyCode, VATNumber, UseTaxInclusivePricing and PostalCode
  parsedLocations.forEach((location, index) => {
    const validCountryOption =
      location.Country && Object.values(CountryEnum).includes(location.Country);
    const validCurrencyOption =
      location.CurrencyCode &&
      Object.values(Currency).includes(location.CurrencyCode);

    if (!validCurrencyOption) {
      if (location.CurrencyCode) {
        errors.push({
          code: z.ZodIssueCode.custom,
          message: `${location.CurrencyCode} is not an accepted currency value`,
          path: [index, 'CurrencyCode'],
        });
      }
    }
    if (
      location?.Country === CountryEnum['United States'] ||
      location?.Country === CountryEnum['Canada']
    ) {
      if (
        location?.UseTaxInclusivePricing ===
        TaxPricingOption['Always Include Tax']
      ) {
        errors.push({
          code: z.ZodIssueCode.custom,
          message:
            'Tax inclusive pricing can’t be enabled for locations in the United States and Canada.',
          path: [index, 'UseTaxInclusivePricing'],
        });
      }
      if (location?.BusinessNumber && location?.BusinessNumber?.trim().length) {
        errors?.push({
          code: z.ZodIssueCode.custom,
          message:
            'Business Number can’t be configured for locations in the United States and Canada.',
          path: [index, 'BusinessNumber'],
        });
      }
    }
    if (validCountryOption) {
      const selectedCountry =
        Object.keys(CountryEnum)[
          Object.values(CountryEnum).indexOf(location.Country)
        ];

      if (location.PostalCode) {
        const isValidZipCode = validatePostalCode(
          location.Country,
          location.PostalCode,
        );

        if (!isValidZipCode)
          errors.push({
            code: z.ZodIssueCode.custom,
            message: `Zip/Postal code not valid for country: ${selectedCountry}`,
            path: [index, 'PostalCode'],
          });
      }

      if (validCurrencyOption) {
        const isValidCurrencySelectedForSelectedCountry =
          allowedCurrenciesPerCountry[location.Country].includes(
            location.CurrencyCode,
          );

        const selectedCurrency =
          Object.keys(Currency)[
            Object.values(Currency).indexOf(location.CurrencyCode)
          ];

        if (!isValidCurrencySelectedForSelectedCountry)
          errors.push({
            code: z.ZodIssueCode.custom,
            message: `For the country ${selectedCountry}, the selected currency ${selectedCurrency} is not valid`,
            path: [index, 'CurrencyCode'],
          });
      }
    }
  });

  if (errors && errors.length > 0) {
    return {
      locations: parsedLocations,
      errors: errors,
    };
  }

  return {
    locations: parsedLocations,
    errors: null,
  };
};

export const parseLocations = (
  files: File[],
): Promise<{ locations: Location[]; count: number }> => {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(files[0], {
      beforeFirstChunk: (chunk) => {
        // This validation serves to check whether headers or a group of headers are in the first row
        const lines = chunk.split(/\r\n|\r|\n/);
        const firstRow = lines[0].split(',');
        const firstHeaderElement = firstRow[0];

        // If first row are group of headers, then we use this piece of code to remove the group so that we can use the first row as the keys of our table
        if (
          firstHeaderElement !== 'Location Name*' &&
          firstHeaderElement !== 'Location Name'
        ) {
          const modifiedChunk = lines.slice(1).join('\n');
          return modifiedChunk;
        }

        return chunk;
      },
      complete: (result) => {
        // Filter out rows with all blank fields
        let results = result?.data?.filter(
          (result) => !Object.values(result).every((value) => !value),
        );
        const totalLocationsCount = results.length;

        if (!results.length || !result?.data[0]) {
          results = [{}];
        }

        const { locations, errors } = validateAndParseEntry(results);
        if (!errors) {
          resolve({ locations, count: totalLocationsCount });
        } else {
          const errorsParse = errors.map((e) => {
            return {
              row: String(Number(e.path[0]) + 1),
              locationName: locations[Number(e.path[0])].Name,
              columnInError: Object.keys(mapCsvToLocation)
                .find((el) => mapCsvToLocation[el].key === e.path[1])
                ?.replace('(if applicable)', '')
                .trim(),
              error: e.message,
            };
          });

          const sortedErrors = _.sortBy(errorsParse, 'row');

          console.error('Validation failed. Errors:', errors);
          reject({ errors: sortedErrors, count: totalLocationsCount });
        }
      },
      error: () => {
        reject({
          errors: ['An unexpected error occurred during validation'],
          count: 0,
        });
      },
      header: true,
      skipEmptyLines: true,
    });
  });
};
